import { Link, useParams } from 'react-router-dom'
import { site, RECYCLING_ROUTE } from '@/data/content'
import { recyclingContent } from '@/data/recycling'
import { getRecyclingTopic, recyclingTopicList } from '@/data/recyclingTopics'
import RecyclingHeroPromo from '@/components/RecyclingHeroPromo'
import RecyclingQuoteForm from '@/components/RecyclingQuoteForm'
import NotFound from '@/pages/NotFound'
import { usePageSeo } from '@/hooks/usePageSeo'
import { localBusinessJsonLd } from '@/data/seo'
import './Recycling.css'
import '@/components/Hero.css'

export default function RecyclingTopicPage() {
  const { topicSlug } = useParams<{ topicSlug: string }>()
  const topic = getRecyclingTopic(topicSlug)

  usePageSeo(
    topic
      ? {
          title: topic.title,
          description: topic.description,
          path: `/recycling/${topic.slug}`,
          keywords: `${topic.h1}, recycling London, e-waste, SecureFix, environment`,
          image: topic.image ?? '/images/recycling-hero.png',
          jsonLd: [
            localBusinessJsonLd(),
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://securefix.uk/' },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Recycling',
                  item: 'https://securefix.uk/recycling',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: topic.h1,
                  item: `https://securefix.uk/recycling/${topic.slug}`,
                },
              ],
            },
          ],
        }
      : {
          title: 'Recycling | SecureFix',
          description: 'SecureFix recycling',
          path: '/recycling',
        },
  )

  if (!topic) return <NotFound />

  const related = recyclingTopicList.filter((item) => item.slug !== topic.slug).slice(0, 4)

  return (
    <div className="recycle-page site-page">
      <section className="hero page-hero" aria-label={topic.h1}>
        <div className="hero-bg" />
        <div className="container hero-layout">
          <div className="hero-content">
            <p className="hero-eyebrow">
              <Link to={RECYCLING_ROUTE} className="recycle-hero-back">
                ← Recycling
              </Link>
              {' · '}
              Secure · Compliant · Sustainable
            </p>
            <h1>
              <span className="hero-line">{topic.h1}</span>
              {topic.accent ? <span className="hero-line accent">{topic.accent}</span> : null}
            </h1>
            <p className="hero-subtitle">{topic.lead}</p>
            <div className="hero-actions">
              <a href="#quote" className="btn btn-primary btn-shiny">
                Request a Quote
              </a>
              <a href="#process" className="btn btn-secondary btn-shiny">
                Our Recycling Process
              </a>
            </div>
            <div className="hero-stats">
              {recyclingContent.trustBadges.slice(0, 2).map((badge) => (
                <div className="hero-stat" key={badge.label}>
                  <strong>{badge.label}</strong>
                  <span>SecureFix recycling standard</span>
                </div>
              ))}
            </div>
          </div>
          <div className="hero-visual">
            <RecyclingHeroPromo label={topic.h1} />
          </div>
        </div>
      </section>

      <main className="recycle-main">
        <div className="container">
          <section className="recycle-section">
            <h2>
              Why <span className="accent">recycle</span>?
            </h2>
            {topic.environment.map((para) => (
              <p key={para.slice(0, 48)} className="recycle-lead">
                {para}
              </p>
            ))}
            <div className="recycle-reasons recycle-topic-benefits">
              {topic.benefits.map((benefit) => (
                <article key={benefit.title} className="recycle-reason">
                  <span>{benefit.icon}</span>
                  <div>
                    <h3>{benefit.title}</h3>
                    <p>{benefit.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section id="process" className="recycle-section">
            <h2>
              Our Recycling <span className="accent">Process</span>
            </h2>
            <p className="recycle-lead">
              Every {topic.h1.toLowerCase()} request follows the same SecureFix recycling process as
              our main recycling service.
            </p>
            <div className="recycle-process">
              {recyclingContent.process.map((step) => (
                <article key={step.step} className="recycle-process-step">
                  <span className="recycle-process-icon">{step.icon}</span>
                  <span className="recycle-process-num">{step.step}</span>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="recycle-section">
            <h2>
              What we <span className="accent">cover</span>
            </h2>
            <ul className="recycle-topic-bullets">
              {topic.bullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="recycle-section recycle-split">
            <div>
              <h2>
                Ready to recycle with <span className="accent">SecureFix</span>
              </h2>
              <p className="recycle-lead">
                Tell us what you need to recycle. We will confirm collection or drop-off options and
                how we protect your data.
              </p>
              <p>
                Prefer to talk it through? Call{' '}
                <a href={site.phoneHref}>{site.phone}</a> or email{' '}
                <a href={`mailto:${site.email}`}>{site.email}</a>.
              </p>
            </div>

            <div id="quote" className="recycle-quote">
              <h2>
                Request a <span className="accent">Quote</span>
              </h2>
              <RecyclingQuoteForm
                defaultEquipment={topic.equipmentDefault}
                topicLabel={topic.h1}
              />
            </div>
          </section>

          {related.length > 0 && (
            <section className="recycle-section">
              <h2>
                Related recycling <span className="accent">topics</span>
              </h2>
              <div className="recycle-categories">
                {related.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/recycling/${item.slug}`}
                    className="recycle-category-card recycle-category-card-link"
                  >
                    <h3>{item.h1}</h3>
                    <p>{item.description}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="recycle-cta">
            <div>
              <span className="recycle-cta-icon">♻️</span>
              <h2>Recycling with SecureFix is good for the environment</h2>
              <p>
                Secure data handling first, then responsible recovery — so your devices help the
                planet instead of filling landfill.
              </p>
            </div>
            <div className="recycle-cta-actions">
              <a href="#quote" className="btn btn-primary btn-shiny">
                Request a Quote
              </a>
              <Link to={RECYCLING_ROUTE} className="btn btn-secondary">
                Back to Recycling
              </Link>
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}
